// ==============================|| CONTRACT TYPES ||============================== //

import { ClientIdentificationDto, RelatedPartiesDto } from './clientIdentification';
import { Company } from './companies';
import { PartyType, RelationshipTypeCode } from './contacts';
import { CustomField, CustomFieldDto, CustomFieldValue } from './customFields';
import { DocumentTemplateMinDto } from './document.ts';
import { FinancialCondition } from './financial-conditions';
import { Office } from './offices';
import { MinDto, NamedEntityMinDto, NumericIdPrimKeyDto } from './other';
import {
    ConnectedMatterDto,
    HourlyRate,
    Pricelist,
    PricelistPeriodWrite,
    PricingKind,
    StagedPricelistPeriod,
} from './pricelists';

export enum ContractStatusType {
    DRAFT = 'DRAFT',
    VALID = 'VALID',
    TERMINATED = 'TERMINATED',
}

export enum ContractType {
    AML = 'AML',
    CLIENT_IDENTIFICATION = 'CLIENT_IDENTIFICATION',
    COOPERATION = 'COOPERATION',
    REPRESENTATION = 'REPRESENTATION',
    SERVICE_AGREEMENT = 'SERVICE_AGREEMENT',
    UBO = 'UBO',
}

export interface ContractListItem {
    id: number;
    /** `null` on a draft saved before its company was chosen. */
    tenantCompany: Company | null;
    contractName: string;
    contractStatusTypeCode: string;
    contractTypeCode: string;
    dmsLink?: string;
    dmsNumber?: string;
    contractSignedAt?: Date;
    companies?: ContractCompany[];
    parties?: ContractParty[];
    pricelist: Pricelist;
    pricelistCurrencyTypeCode?: string;
    matters: ConnectedMatterDto[];
    contractInvoicingIntervalTypeCode: string;
    customFieldValues: CustomFieldValue[];
    isDraft?: boolean;
}

/** A case's service agreement as the case list column renders it. */
export interface ServiceAgreementListItemDto {
    id: number;
    contractName: string | null;
    tenantCompanyName: string | null;
    contractSignedAt: string | null;
    contractStatusTypeCode: string | null;
}

export interface ContractListItemMinDto {
    id: number;
    contractName: string;
    nameExtension?: string | null;
    contractStatusTypeCode: string;
    pricelistCurrencyTypeCode: string;
    contractInvoicingIntervalTypeCode: string;
    isClientTemplate: boolean;
    dmsLink: string | null;
    discountPct?: string;
    createdAt?: string;
    contractSignedAt?: string;
    pricelistName?: string;
    minHourlyPrice?: string;
    maxHourlyPrice?: string;
    avgHourlyPrice?: string;
    pricelistId?: number;
}

export interface Contract {
    id: number;
    billingNotes: string | null;
    createdAt: Date;
    lastModifiedAt: Date;
    createdByEmployee?: { id: number; name: string };
    changedByEmployee?: { id: number; name: string };
    /** `null` on a draft saved before its company was chosen. */
    tenantCompany: Company | null;
    contractName: string;
    nameExtension: string | null;
    contractTypeCode: string;
    contractStatusTypeCode: string;
    documentTemplateTypeCode: string;
    contractNumber?: string;
    dmsNumber?: string;
    dmsLink?: string;
    contractCreatedAt?: Date;
    contractSignedAt?: Date;
    contractSentAt?: Date;
    contractTerminatedAt?: Date;
    discountPct?: string;
    discountExpiresAt?: Date;
    financialCondition: FinancialCondition;
    clientReference?: string;
    timecardSpecificationLayoutTypeCode?: string;
    timecardIncrement?: number | null;
    storedInOffice: Office;
    signedByPersonParty?: {
        id: number;
        name: string;
        firstName?: string;
        lastName?: string;
    };
    companies?: ContractCompany[];
    parties?: ContractParty[];
    pricelist?: Pricelist;
    customFields?: CustomField[];
    invoicingInfo: string | null;
    isClientTemplate: boolean;
    contractTemplate: ContractTemplateDto;
    beneficialOwners: NamedEntityMinDto[];
    beneficialOwnerNotes: string | null;
    contractRelatedKeywords: ContractRelatedKeywordDto[];
    adminFeePercentage?: string;
    additionalNotes: string | null;
    connectedMatters?: ConnectedMatterDto[];
    bankAccount: MinDto;
    isDeletable: boolean;
    isDraft?: boolean;
    /** On a client identification only. */
    assessment?: ClientIdentificationDto | null;
    /** On a client identification only. */
    relatedParties?: RelatedPartiesDto | null;
}

interface ContractCompany {
    relationshipTypeCode: RelationshipTypeCode;
    company: Company;
}

export interface ContractParty {
    orderNum: number;
    party: {
        id: number;
        name: string;
        firstName?: string;
        lastName?: string;
        partyType: PartyType;
        relationshipTypeCode: RelationshipTypeCode;
    };
}

export interface ContractCreateDto {
    contractTypeCode: string | null;
    nameExtension: string | null;
    documentTemplateTypeCode: string | null;
    contractTemplateId: number | null;
    isClientTemplate: boolean;
    tenantCompanyId: number | null;
    signedByPersonPartyId: number | null;
    companies: NumericIdPrimKeyDto[] | null;
    parties: NumericIdPrimKeyDto[] | null;
    clientReference: string | null;
    contractNumber: string | null;
    dmsLink: string | null;
    dmsNumber: string | null;
    contractSentAt: Date | null;
    contractSignedAt: Date | null;
    contractTerminatedAt: Date | null;
    financialConditionId: number | null;
    paymentDeadlineInDays: number | null;
    prepaymentPaymentDeadlineInDays: number | null;
    invoicingIntervalTypeCode: string | null;
    discountPct: string | null;
    interestRatePct: string | null;
    interestRateB2cPct: string | null;
    currencyTypeCode: string | null;
    timecardSpecificationLayoutTypeCode: string | null;
    timecardIncrement: number | null;
    pricelistId: number | null;
    pricelist: {
        name: string;
        scopeTypeCode: string;
        currencyTypeCode: string | null;
        rates: HourlyRate[];
        pricingKind?: PricingKind;
        periods?: PricelistPeriodWrite[];
        followsPricelist?: { id: number } | null;
    } | null;
    customFields?: CustomFieldDto[];
    billingNotes: string | null;
    invoicingInfo: string | null;
    beneficialOwners: number[] | null;
    beneficialOwnerNotes: string | null;
    contractRelatedKeywords: Pick<ContractRelatedKeywordDto, 'contractRelatedKeywordCode'>[];
    additionalNotes: string | null;
    adminFeePercentage: string | null;
    bankAccountId: number | null;
}

export interface ContractRelatedKeywordDto {
    id: number;
    contractRelatedKeywordCode: string;
}

export interface ContractUpdateDto extends ContractCreateDto {
    id: string | number;
}

export interface ContractForm {
    billingNotes: string | null;
    contractName: string;
    nameExtension: string | null;
    contractType: { label: string; value: string } | null;
    documentTemplateType: { label: string; value: string } | null;
    isClientTemplate: boolean;
    contractTemplate: ContractTemplateDto | null;
    tenantCompany: NamedEntityMinDto | null;
    signedByPersonParty: NamedEntityMinDto | null;
    companies: NamedEntityMinDto[] | null;
    parties:
        | {
              id: number;
              name: string;
              firstName?: string;
              lastName?: string;
              partyType: PartyType;
              relationshipTypeCode: RelationshipTypeCode;
          }[]
        | null;
    clientReference: string | null;
    contractNumber: string | null;
    dmsLink: string | null;
    dmsNumber: string | null;
    contractSentAt: string | null;
    contractSignedAt: string | null;
    contractTerminatedAt: string | null;
    financialConditionId: number | null;
    paymentDeadlineInDays: number | null;
    prepaymentPaymentDeadlineInDays: number | null;
    invoicingInfo: string | null;
    invoicingIntervalType: { label: string; value: string } | null;
    discountPct: string | null;
    interestRatePct: string | null;
    interestRateB2cPct: string | null;
    currencyType: { label: string; value: string } | null;
    timecardSpecificationLayoutType: { label: string; value: string } | null;
    timecardIncrement: { label: string; value: number } | null;
    pricelistId: number | null;
    pricelist: {
        name: string;
        scopeTypeCode: string;
        currencyTypeCode: string | null;
        pricingKind?: PricingKind;
    } | null;
    pricelistRates: HourlyRate[];
    // The periods of an employee-priced list as the form stages them; absent on a role-priced one.
    pricelistPeriods?: StagedPricelistPeriod[] | null;
    // The model an employee-priced list follows; null for a list with its own prices.
    pricelistFollows?: { id: number; name: string } | null;
    // Set once the pricing is changed in the form: only then does a save send the periods or the link.
    pricelistEdited?: boolean;
    pricingHash: string | undefined;
    readOnlyCreatedAt: string | null;
    beneficialOwners: NamedEntityMinDto[] | null;
    beneficialOwnerNotes: string | null;
    contractRelatedKeywords: { label: string; value: string }[];
    adminFeePercentage: string | null;
    additionalNotes: string | null;
    bankAccount: MinDto | null;
}

export interface ContractTemplateDto extends DocumentTemplateMinDto {
    contractTypeCode: string;
}

export interface ContractMinDetailsDto {
    id: number;
    contractNumber: string | null;
    contractSentAt: string | null;
    contractSignedAt: string | null;
}
